import { StanButton, StanModal } from '@/components';
import { createIconElement } from '@/lib/utils/createIconElement';
import { formatTimeTo12Hour, parseLocalDate } from '@/lib/utils/timeUtils';
import IcCalendar from '@/assets/icons/ic-calendar.svg';
import IcClock from '@/assets/icons/ic-clock.svg';

const CalendarIcon = createIconElement(IcCalendar, { className: 'size-5' });
const ClockIcon = createIconElement(IcClock, { className: 'size-5' });

interface DeleteBlockedTimeModalProps {
    isOpen: boolean;
    onClose: () => void;
    blockedTime: {
        startDate: string;
        endDate: string;
        startTime: string;
        endTime: string;
    };
    onConfirm: () => Promise<void>;
    isDeleting?: boolean;
}

export function DeleteBlockedTimeModal({
    isOpen,
    onClose,
    blockedTime,
    onConfirm,
    isDeleting = false,
}: DeleteBlockedTimeModalProps) {
    const formatDate = (dateString: string) => {
        const date = parseLocalDate(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const handleConfirm = async () => {
        await onConfirm();
        onClose();
    };

    return (
        <StanModal
            id='delete-blocked-time-modal'
            isOpen={isOpen}
            onClose={onClose}
            title='Are you sure?'
            size='sm'
            hideCloseButton
        >
            <div className='space-y-6'>
                <p className='text-secondary text-body-sm text-center'>
                    If you delete this block, your calendar will become available during this time.
                </p>

                <div className='flex gap-8'>
                    {/* Starting */}
                    <div className='flex-1 space-y-3'>
                        <h5 className='text-secondary text-body-xs font-medium'>Starting</h5>
                        <div className='space-y-2'>
                            <div className='flex items-center gap-3'>
                                {CalendarIcon}
                                <span className='text-dark text-body-xs'>{formatDate(blockedTime.startDate)}</span>
                            </div>
                            <div className='flex items-center gap-3'>
                                {ClockIcon}
                                <span className='text-dark text-body-xs'>
                                    {formatTimeTo12Hour(blockedTime.startTime)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Ending */}
                    <div className='flex-1 space-y-3'>
                        <h5 className='text-secondary text-body-xs font-medium'>Ending</h5>
                        <div className='space-y-2'>
                            <div className='flex items-center gap-3'>
                                {CalendarIcon}
                                <span className='text-dark text-body-xs'>{formatDate(blockedTime.endDate)}</span>
                            </div>
                            <div className='flex items-center gap-3'>
                                {ClockIcon}
                                <span className='text-dark text-body-xs'>
                                    {formatTimeTo12Hour(blockedTime.endTime)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='flex gap-3 pt-4'>
                    <StanButton
                        name='confirm-delete-block-time'
                        onClick={handleConfirm}
                        color='destructive'
                        className='flex-1'
                        disabled={isDeleting}
                        loading={isDeleting}
                    >
                        Remove
                    </StanButton>
                    <StanButton
                        name='cancel-delete-block-time'
                        onClick={onClose}
                        color='secondary'
                        className='flex-1'
                        disabled={isDeleting}
                    >
                        Cancel
                    </StanButton>
                </div>
            </div>
        </StanModal>
    );
}
